import debugPkg from "debug";
import { debounce } from "lodash-es";
import { err, ok, Result, safeTry } from "neverthrow";

import {
  defined,
  IncidentWorkItemStatusSchema,
  parsed,
  type IncidentWorkItemStatus,
} from "@thalamos/common";

import { FormSectionSchema } from "../types/FormSection.js";
import {
  IncidentFormTypeSchema,
  type IncidentWorkItemWithContext,
} from "../types/incidentWorkItem.js";
import type { RequestPolicePresenceMinimumFields } from "../types/workflow/minimumFields/policePresence.js";
import type { RecordPlaceOfSafetyArrivalMinimumFields } from "../types/workflow/minimumFields/recordPlaceOfSafety.js";
import { workflows } from "../workflowDefinitions/workflows.js";

const { debug: debugFn } = debugPkg;
const debug = debugFn("vision-common:calculateIncidentWorkItemStatus");

/// In-code feature switch to control whether to log errors to the console and DataDog.
const logErrors = debug.enabled;

const debounceDebug = logErrors ? debounce(debug, 1000) : undefined;
const debounceConsoleError = logErrors
  ? debounce(console.error, 1000)
  : undefined;
const debounceConsoleWarn = logErrors
  ? debounce(console.warn, 1000)
  : undefined;

/**
 * Calculates the status of an incident work item based on the forms contained within.
 * @param incidentWorkItem
 * @param sourceOfTruth - Determines whether to use the incoming value or the calculated value.
 * @returns
 */
export function calculateIncidentWorkItemStatus(
  incidentWorkItem: IncidentWorkItemWithContext,
  sourceOfTruth: "incoming-value" | "calculated-value" = "incoming-value",
) {
  const calculatedStatus = safeTry(function* () {
    const workflow = yield* defined(
      workflows[incidentWorkItem.incidentWorkItem.workflowVersion],
      "Could not find workflow" as const,
    );

    // If there is a completed finish form then we must be "ended"
    {
      const finishFormSchema = yield* defined(
        workflow.finishForm.formDefinition.sections[0].buildFormSpecification(
          {},
        ).formSchema,
        "Could not find finish form schema" as const,
      );

      const completeFinishFormData = incidentWorkItem.forms
        .filter(
          (form) => form.formType === IncidentFormTypeSchema.enum.finishForm,
        )
        .some(
          (form) =>
            finishFormSchema.safeParse(
              form.sections.find(
                (section) => section.id === FormSectionSchema.enum.finishForm,
              )?.data,
            ).success,
        );

      if (completeFinishFormData) {
        return ok(IncidentWorkItemStatusSchema.enum.ended);
      }
    }

    // Look for a police presence requested form
    {
      const policePresenceRequestedFormSchema = yield* defined(
        workflow.requestPolicePresence.formDefinition.sections[0].buildFormSpecification(
          {},
        ).formSchema,
        "Could not find police presence requested form schema" as const,
      );

      const completePolicePresenceRequestedFormData = incidentWorkItem.forms
        .filter(
          (form) =>
            form.formType === IncidentFormTypeSchema.enum.requestPolicePresence,
        )
        .map((form) =>
          parsed(
            policePresenceRequestedFormSchema,
            form.sections.find(
              (section) =>
                section.id === FormSectionSchema.enum.requestPolicePresenceMain,
            )?.data,
          )
            .map(
              (data: RequestPolicePresenceMinimumFields) =>
                data.hasPolicePresenceRequested.value === "yes",
            )
            .unwrapOr(false),
        );

      if (completePolicePresenceRequestedFormData.length > 0) {
        return completePolicePresenceRequestedFormData.some((data) => data)
          ? ok(IncidentWorkItemStatusSchema.enum["police-presence-requested"])
          : ok(
              IncidentWorkItemStatusSchema.enum[
                "police-presence-not-requested"
              ],
            );
      }
    }

    // TODO: This is probably where we should check for IncidentWorkItemStatus.nhsFormAccepted
    // but there isn't any form data which records this.

    // Look for a "share" form
    {
      const shareFormSchema = yield* defined(
        workflow.shareIncident.formDefinition.sections[0].buildFormSpecification(
          {},
        ).formSchema,
        "Could not find share form schema" as const,
      );

      const completeShareFormData = incidentWorkItem.forms
        .filter(
          (form) => form.formType === IncidentFormTypeSchema.enum.shareIncident,
        )
        .some(
          (form) =>
            shareFormSchema.safeParse(
              form.sections.find(
                (section) =>
                  section.id === FormSectionSchema.enum.shareIncident,
              )?.data,
            ).success,
        );

      if (completeShareFormData) {
        return ok(IncidentWorkItemStatusSchema.enum["form-shared"]);
      }
    }

    // Look for a "Place of Safety" forms
    {
      const recordPlaceOfSafetyFormSchema = yield* defined(
        workflow.recordPlaceOfSafety.formDefinition.sections[0].buildFormSpecification(
          {},
        ).formSchema,
        "Could not find record place of safety form schema" as const,
      );

      const arrivePlaceOfSafetyFormSchema = yield* defined(
        workflow.recordPlaceOfSafety.formDefinition.sections[1].buildFormSpecification(
          {},
        ).formSchema,
        "Could not find arrive place of safety form schema" as const,
      );

      const completePlaceOfSafetyArrivalForms = incidentWorkItem.forms
        .filter(
          (form) =>
            form.formType === IncidentFormTypeSchema.enum.recordPlaceOfSafety &&
            parsed(
              recordPlaceOfSafetyFormSchema,
              form.sections.find(
                (section) =>
                  section.id === FormSectionSchema.enum.recordPlaceOfSafetyMain,
              )?.data,
            )
              .map(() => true)
              .unwrapOr(false),
        )
        .map((form) =>
          parsed(
            arrivePlaceOfSafetyFormSchema,
            form.sections.find(
              (section) =>
                section.id ===
                FormSectionSchema.enum.recordPlaceOfSafetyArrival,
            )?.data,
          )
            .map(
              (data: RecordPlaceOfSafetyArrivalMinimumFields) =>
                data.didNotArrive?.value === "yes",
            )
            .map((didNotArrive) => !didNotArrive)
            .unwrapOr(false),
        );

      if (completePlaceOfSafetyArrivalForms.length > 0) {
        return completePlaceOfSafetyArrivalForms.some((data) => data)
          ? ok(IncidentWorkItemStatusSchema.enum["arrived-at-place-of-safety"])
          : ok(IncidentWorkItemStatusSchema.enum["awaiting-arrival"]);
      }
    }

    // Look for a complete "Triage" form
    {
      const triageFormSchema = yield* defined(
        workflow.triageIncident.formDefinition.sections[0].buildFormSpecification(
          {},
        ).formSchema,
        "Could not find triage form schema" as const,
      );

      const completeTriageFormData = incidentWorkItem.forms
        .filter((form) => form.formType === IncidentFormTypeSchema.enum.triage)
        .some(
          (form) =>
            triageFormSchema.safeParse(
              form.sections.find(
                (section) => section.id === FormSectionSchema.enum.triageMain,
              )?.data,
            ).success,
        );

      if (completeTriageFormData) {
        return ok(IncidentWorkItemStatusSchema.enum["in-progress"]);
      }
    }

    // Look for a complete "Create Incident" form
    {
      const createIncidentFormSchema = yield* defined(
        workflow.createIncident.formDefinition.sections[0].buildFormSpecification(
          {},
        ).formSchema,
        "Could not find create incident form schema" as const,
      );
      const completeCreateIncidentFormData = incidentWorkItem.forms
        .filter(
          (form) =>
            form.formType === IncidentFormTypeSchema.enum.createIncident,
        )
        .some(
          (form) =>
            createIncidentFormSchema.safeParse(
              form.sections.find(
                (section) =>
                  section.id === FormSectionSchema.enum.createIncidentMain,
              )?.data,
            ).success,
        );

      if (completeCreateIncidentFormData) {
        return ok(IncidentWorkItemStatusSchema.enum["unknown"]);
      }
    }

    return err("Unable to determine incident work item status" as const);
  })
    .orTee((error) => {
      const errorMessage = `ASP-3430 Error calculating incident work item status for ${incidentWorkItem.incidentWorkItem.id}: ${error}`;
      debounceDebug?.(errorMessage);

      // We console.error this so that we can see it in DataDog.
      // This will inform whether this is a suitable replacement for the stateful solution.
      debounceConsoleError?.(errorMessage);
    })
    .andTee((result) => {
      if (result !== incidentWorkItem.incidentWorkItem.status) {
        const errorMessage = `ASP-3430 Calculated status '${result}' does not match incoming status '${incidentWorkItem.incidentWorkItem.status}' for incident work item ${incidentWorkItem.incidentWorkItem.id}`;
        debounceDebug?.(errorMessage);

        // We console.error this so that we can see it in DataDog.
        // This will inform whether this is a suitable replacement for the stateful solution.
        // We treat "nhs-form-accepted" as a warning because we know we can't calculate that.
        if (
          incidentWorkItem.incidentWorkItem.status ===
          IncidentWorkItemStatusSchema.enum["nhs-form-accepted"]
        ) {
          debounceConsoleWarn?.(errorMessage);
        } else {
          debounceConsoleError?.(errorMessage);
        }
      }
    });

  // If the source of truth is the incoming value, we return that
  return (
    sourceOfTruth === "incoming-value"
      ? ok(incidentWorkItem.incidentWorkItem.status)
      : calculatedStatus
  ) satisfies Result<IncidentWorkItemStatus, string>;
}
